if (typeof QuickView === 'undefined') {
  const { FoxThemeStyles, FoxThemeScripts } = window;

  // SAUV - "How it works" video overlay for quick view.
  // Quick view HTML is fetched and appended by this script, so the inline
  // <script> inside snippets/product-media.liquid never runs here - this is
  // the code that actually controls the video in quick view.
  //  - Covers the photo that's actually showing (fixes products like the
  //    Everyday Long Sleeves, whose quick view opens on photo 3).
  //  - Hides as soon as the shopper clicks a thumbnail, an arrow, swipes, or
  //    picks a color that changes the photo.
  const initHowItWorksVideo = (video) => {
    if (!video || video.dataset.sauvHiwInit) return;
    video.dataset.sauvHiwInit = 'true';

    const viewer = video.closest('[data-media-viewer]');
    const slider = viewer ? viewer.querySelector('flickity-component') : null;
    // media-gallery wraps both the main photo frame and the thumbnail strip
    const gallery = video.closest('media-gallery') || viewer;
    const modal = video.closest('modal-dialog');
    if (!viewer || !slider) return;

    // Player controls (unmute/scrub) on desktop only. On phones the native
    // controls dim the video and sit under the carousel arrows.
    if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      video.controls = true;
    }

    let dismissed = false;
    // Flips on the shopper's first click/tap/keypress after quick view opens.
    // Before that, slide changes are the theme settling on the default
    // variant's photo, so the video follows. After it, a slide change means
    // the shopper moved on, so the video gets out of the way.
    let armed = false;
    let lastCell = null;
    let userPaused = false;
    const timers = [];
    let mo = null;
    let ro = null;

    const cells = () => slider.querySelectorAll('.f-product__media');

    // Carousel: cover the slide that's showing. Grid (Flickity off): first photo.
    const currentCell = () => {
      if (slider.classList.contains('flickity-enabled')) {
        const sel = slider.querySelector('.f-product__media.is-selected');
        if (sel) return sel;
      }
      return cells()[0] || null;
    };

    const position = () => {
      if (dismissed) return;
      const cell = currentCell();
      if (!cell) return;
      const vr = viewer.getBoundingClientRect();
      const cr = cell.getBoundingClientRect();
      if (!cr.width || !cr.height) return; // modal still hidden
      // Mobile: lock the video to a 5:7 frame (matches the photos) instead
      // of stretching to the slide's full height.
      const h = window.matchMedia('(max-width: 767.98px)').matches
        ? (cr.width * 7) / 5
        : cr.height;
      video.style.top = `${cr.top - vr.top}px`;
      video.style.left = `${cr.left - vr.left}px`;
      video.style.width = `${cr.width}px`;
      video.style.height = `${h}px`;
      video.style.opacity = '1';
    };

    const arm = () => {
      armed = true;
    };

    const onGalleryPointer = (e) => {
      // A mouse click on the video itself is for its controls - don't hide.
      if (e.target === video && e.pointerType === 'mouse') return;
      dismiss();
    };

    const onGalleryClick = (e) => {
      if (e.target === video) return;
      dismiss();
    };

    const onSelectionChange = () => {
      const cell = currentCell();
      if (cell === lastCell) return;
      lastCell = cell;
      if (armed) {
        dismiss();
      } else {
        position();
        timers.push(setTimeout(position, 60), setTimeout(position, 500));
      }
    };

    const attemptPlay = () => {
      if (dismissed || userPaused) return;
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          // Autoplay blocked (e.g. iPhone Low Power Mode): show the photos
          // instead of a frozen video box.
          if (err && err.name === 'NotAllowedError') dismiss();
        });
      }
    };

    const onVisibility = () => {
      if (!document.hidden) attemptPlay();
    };

    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      video.pause();
      video.style.display = 'none';
      cleanup();
    }

    function cleanup() {
      timers.forEach(clearTimeout);
      if (mo) mo.disconnect();
      if (ro) ro.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('orientationchange', position);
      document.removeEventListener('pointerdown', arm, true);
      document.removeEventListener('keydown', arm, true);
      document.removeEventListener('visibilitychange', onVisibility);
      gallery.removeEventListener('pointerdown', onGalleryPointer, true);
      gallery.removeEventListener('click', onGalleryClick, true);
    }

    lastCell = currentCell();
    position();
    // Flickity / the modal can still be laying out right after open.
    [50, 150, 400, 1000, 2000].forEach((d) => {
      timers.push(
        setTimeout(() => {
          onSelectionChange();
          position();
        }, d)
      );
    });

    if ('MutationObserver' in window) {
      mo = new MutationObserver(onSelectionChange);
      mo.observe(slider, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(position);
      ro.observe(viewer);
    }
    window.addEventListener('resize', position);
    window.addEventListener('orientationchange', position);
    document.addEventListener('pointerdown', arm, true);
    document.addEventListener('keydown', arm, true);
    document.addEventListener('visibilitychange', onVisibility);
    gallery.addEventListener('pointerdown', onGalleryPointer, true);
    gallery.addEventListener('click', onGalleryClick, true);

    // Keep it playing until dismissed (the slider / hidden-modal autoplay
    // rules can pause it on their own), unless the shopper paused it.
    video.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') userPaused = true;
    });
    video.addEventListener('play', () => {
      userPaused = false;
    });
    video.addEventListener('pause', attemptPlay);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              position();
              attemptPlay();
            }
          });
        },
        { threshold: 0.15 }
      ).observe(video);
    }
    attemptPlay();

    // The quick view modal is removed on close - drop document listeners.
    if (modal) modal.addEventListener('close', cleanup, { once: true });
  };

  class QuickView2 {
    constructor() {
      this.modal = null;
      addEventDelegate({
        selector: '[data-product-quickview]',
        handler: (e, target) => {
          e.preventDefault();
          this.target = target;
          target.classList.add('btn--loading');
          const productHandle = target.dataset.productQuickview;
          productHandle && this.fetchHtml(productHandle);
        },
      });
      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.modal && this.modal.hide();
      });
    }

    fetchHtml(productHandle) {
      loadAssets([FoxThemeStyles.product, FoxThemeStyles.volumePricing], 'quick-view-assets');
      fetchSection('product-quickview', {
        url: `${window.FoxThemeSettings.base_url}products/${productHandle}`,
      })
        .then((html) => {
          this.modal = html.querySelector('modal-dialog');
          const firstModel = html.querySelector('product-model');
          this.mediaGallery = this.modal.querySelector('media-gallery');
          document.body.appendChild(this.modal);
          loadAssets(
            [
              FoxThemeScripts.productInfo,
              FoxThemeScripts.productMedia,
              FoxThemeScripts.variantsPicker,
              FoxThemeScripts.volumePricing,
            ],
            'variants-picker',
            () => {
              this.modal && this.modal.show(this.target);
              document.dispatchEvent(
                new CustomEvent('quick-view:open', {
                  detail: { productUrl: `/products/${productHandle}` },
                })
              );
              if (this.mediaGallery) {
                const thumb = this.mediaGallery.querySelector('[id^="GalleryThumbnails"]');
                thumb && thumb.update();
                this.mediaGallery.update();
              }

              initHowItWorksVideo(this.modal.querySelector('.f-how-it-works-video__el'));

              Shopify && Shopify.PaymentButton && Shopify.PaymentButton.init();
              this.target.classList.remove('btn--loading');
              this.handleClose();
              this.toolTip = __reInitTooltip(this.modal);
              document.dispatchEvent(
                new CustomEvent('quick-view:loaded', {
                  detail: { productUrl: `/products/${productHandle}` },
                })
              );
            }
          );
          firstModel &&
            loadAssets(
              [
                FoxThemeScripts.productModel,
                'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css',
              ],
              'product-model-assets'
            );
        })
        .catch(console.error);
    }

    handleClose() {
      this.modal &&
        this.modal.addEventListener('close', () => {
          [
            ...(this.modal.querySelector('product-info')?.toolTip || []),
            ...(this.toolTip || []),
          ].forEach((item) => item.unmount());
          this.modal.querySelectorAll('quantity-input').forEach((input) => {
            input.disconnectedCallback();
          });
          this.modal.remove();
        });
    }
  }

  new QuickView2();
  window.QuickView = QuickView2;
}